import React, { useEffect, useState } from "react";
import api, { fmtDate } from "../lib/api";
import { Page, Field } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const empty = { name: "", phone: "", email: "", address: "", gstin: "", dob: "", anniversary: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/customers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Failed"); }
  };

  const showDetail = async (id) => {
    const { data } = await api.get(`/customers/${id}`);
    setDetail(data);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page
      number="07"
      title="Customers"
      subtitle="Vehicle ownership history, service visits, GST & loyalty — all in one record."
      right={<button className="kk-btn-primary" onClick={() => setOpen(true)} data-testid="add-customer"><Plus size={14}/> Add Customer</button>}
    >
      <table className="kk-table">
        <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>GSTIN</th><th>Loyalty</th><th></th></tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} onClick={() => showDetail(c.id)} className="cursor-pointer" data-testid={`cust-row-${c.id}`}>
              <td className="font-semibold">{c.name}</td>
              <td>{c.phone}</td>
              <td>{c.email}</td>
              <td className="font-mono">{c.gstin || "—"}</td>
              <td className="font-mono">{c.loyalty_points || 0}</td>
              <td><button className="text-xs underline">View</button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[var(--kk-muted)]">No customers yet.</td></tr>}
        </tbody>
      </table>

      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between"><h2 className="font-display text-2xl uppercase">Add Customer</h2><button type="button" onClick={() => setOpen(false)}><X/></button></div>
            <div className="grid grid-cols-2 gap-5 mt-5">
              <div className="col-span-2"><Field label="Name *"><input className="kk-input" required value={form.name} onChange={upd("name")} /></Field></div>
              <Field label="Phone *"><input className="kk-input" required value={form.phone} onChange={upd("phone")} /></Field>
              <Field label="Email"><input className="kk-input" type="email" value={form.email} onChange={upd("email")} /></Field>
              <Field label="GSTIN"><input className="kk-input" value={form.gstin} onChange={upd("gstin")} /></Field>
              <Field label="DOB"><input className="kk-input" type="date" value={form.dob} onChange={upd("dob")} /></Field>
              <Field label="Anniversary"><input className="kk-input" type="date" value={form.anniversary} onChange={upd("anniversary")} /></Field>
              <div className="col-span-2"><Field label="Address"><textarea className="kk-input min-h-[60px]" value={form.address} onChange={upd("address")} /></Field></div>
            </div>
            <button className="kk-btn-primary mt-6" type="submit">Save</button>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between"><h2 className="font-display text-2xl uppercase">{detail.name}</h2><button onClick={() => setDetail(null)}><X/></button></div>
            <div className="text-sm text-[var(--kk-muted)]">{detail.phone} · {detail.email || ""}</div>
            {detail.gstin && <div className="font-mono text-xs mt-1">GSTIN: {detail.gstin}</div>}

            <div className="mt-6">
              <div className="kk-overline">Vehicles</div>
              {(detail.vehicles || []).length === 0 ? <div className="text-sm text-[var(--kk-muted)] mt-1">None on file.</div> :
                <ul className="mt-2 divide-y divide-[var(--kk-border)]">
                  {detail.vehicles.map((v) => (
                    <li key={v.id} className="py-2 flex justify-between">
                      <div>{v.make} {v.model} <span className="text-xs font-mono text-[var(--kk-muted)]">({v.registration_no})</span></div>
                      <div className="text-xs">{v.year}</div>
                    </li>
                  ))}
                </ul>
              }
            </div>

            <div className="mt-6">
              <div className="kk-overline">Service history</div>
              {(detail.service_history || []).length === 0 ? <div className="text-sm text-[var(--kk-muted)] mt-1">No service visits yet.</div> :
                <ul className="mt-2 divide-y divide-[var(--kk-border)]">
                  {detail.service_history.map((j) => (
                    <li key={j.id} className="py-2 flex justify-between text-sm">
                      <div><span className="font-mono">{j.code}</span> · {j.make} {j.model}</div>
                      <div className="text-xs">{fmtDate(j.created_at)}</div>
                    </li>
                  ))}
                </ul>
              }
            </div>

            <div className="mt-6">
              <div className="kk-overline">Warranties</div>
              {(detail.warranties || []).length === 0 ? (
                <div className="text-sm text-[var(--kk-muted)] mt-1">No warranties on record.</div>
              ) : (
                <ul className="mt-2 divide-y divide-[var(--kk-border)]">
                  {detail.warranties.map((w) => (
                    <li key={w.id} className="py-2 flex justify-between items-start text-sm">
                      <div>
                        <div className="font-semibold">{w.title}</div>
                        <div className="text-xs font-mono text-[var(--kk-muted)]">{w.code} · {fmtDate(w.start_date)} → {fmtDate(w.end_date)}</div>
                      </div>
                      <span className={"kk-pill " + (w.status === "active" ? "kk-pill-lime" : "kk-pill-muted")}>{w.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
